import { useCallback, useEffect, useMemo } from "react"

import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import type { RootState } from "../../../../store.ts"
import {
  selectActiveBranchId,
  selectCompanyIsMultiBranch
} from "../../../Branches/redux/branches.slice.ts"
import {
  canDo,
  selectCompanyDefaultsAccess
} from "../../redux/company-defaults-access.selectors.ts"
import { fetchCompanyDefaultsAccess } from "../../redux/company-defaults-access.slice.ts"
import type {
  CompanyDefaultsAccessResponse,
  CompanyDefaultsAccessStatus,
  CompanyDefaultsAction,
  CompanyDefaultsBlockKey
} from "../../types/company-defaults-access.types.ts"
import {
  COMPANY_DEFAULTS_BLOCKS,
  COMPANY_DEFAULTS_SUBTAB_LABELS,
  COMPANY_DEFAULTS_SUBTAB_ORDER,
  COMPANY_DEFAULTS_SUBTAB_ROUTES,
  CONFIGURATION_TABS,
  type CompanyDefaultsSubtab,
  type ConfigurationTab
} from "./company-defaults-blocks.ts"

// Tolerate a store without the branches slice (pages outside Settings that
// render the Automation sub-tabs), the way `useAutomationSections` reads it.
const activeBranchIdOf = (state: RootState) =>
  state.branches ? selectActiveBranchId(state) : null
const companyIsMultiBranchOf = (state: RootState) =>
  state.branches ? selectCompanyIsMultiBranch(state) : false

export interface CompanyDefaultsScope {
  isBranchScope: boolean
  leadSourceBudgetEnabled: boolean
}

export const isBlockShown = (
  access: CompanyDefaultsAccessResponse | null | undefined,
  block: CompanyDefaultsBlockKey,
  scope: CompanyDefaultsScope
) => {
  if (!canDo(access, block, "view")) return false
  const placement = COMPANY_DEFAULTS_BLOCKS[block]
  if (placement.subtab === "configuration") {
    return placement.workspace === (scope.isBranchScope ? "branch" : "company")
  }
  if (placement.subtab === "templates" && placement.needsFeatureFlag) {
    return scope.leadSourceBudgetEnabled
  }
  return true
}

export const visibleSubtabsFor = (
  access: CompanyDefaultsAccessResponse | null | undefined,
  scope: CompanyDefaultsScope
): CompanyDefaultsSubtab[] =>
  COMPANY_DEFAULTS_SUBTAB_ORDER.filter((subtab) =>
    (Object.keys(COMPANY_DEFAULTS_BLOCKS) as CompanyDefaultsBlockKey[]).some(
      (block) =>
        COMPANY_DEFAULTS_BLOCKS[block].subtab === subtab &&
        isBlockShown(access, block, scope)
    )
  )

export const visibleConfigurationTabsFor = (
  access: CompanyDefaultsAccessResponse | null | undefined,
  scope: CompanyDefaultsScope
): ConfigurationTab[] =>
  CONFIGURATION_TABS.filter((tab) =>
    (Object.keys(COMPANY_DEFAULTS_BLOCKS) as CompanyDefaultsBlockKey[]).some(
      (block) => {
        const placement = COMPANY_DEFAULTS_BLOCKS[block]
        return (
          placement.subtab === "configuration" &&
          placement.tab === tab &&
          isBlockShown(access, block, scope)
        )
      }
    )
  )

export function useCompanyDefaultsScope(): CompanyDefaultsScope {
  const isMultiBranch = useAppSelector(companyIsMultiBranchOf)
  const activeBranchId = useAppSelector(activeBranchIdOf)
  const leadSourceBudgetEnabled = useAppSelector(
    (state) => !!state.auth?.userInfo?.feature_marketing_source_performance
  )
  const companyTeamId = useAppSelector(
    selectCompanyDefaultsAccess
  )?.company_team_id
  // The company's HQ team is where the company-wide blocks live, so a member
  // whose workspace is HQ manages those, not HQ's own branch overrides.
  const isBranchScope =
    isMultiBranch && !!activeBranchId && activeBranchId !== companyTeamId
  return useMemo(
    () => ({ isBranchScope, leadSourceBudgetEnabled }),
    [isBranchScope, leadSourceBudgetEnabled]
  )
}

/**
 * What the signed-in user may see and change in Company Defaults for the
 * active workspace, as the server answers it. Fetched once per workspace and
 * again whenever the user's permissions are reloaded.
 */
export function useCompanyDefaultsAccess() {
  const dispatch = useAppDispatch()
  const teamId = useAppSelector(activeBranchIdOf)
  const generation = useAppSelector(
    (state) => state.companyDefaultsAccess?.generation ?? 0
  )
  const sliceStatus = useAppSelector(
    (state) => state.companyDefaultsAccess?.status
  )
  const sliceTeamId = useAppSelector(
    (state) => state.companyDefaultsAccess?.teamId
  )
  const access = useAppSelector(selectCompanyDefaultsAccess)

  useEffect(() => {
    void dispatch(fetchCompanyDefaultsAccess({ teamId }))
  }, [dispatch, teamId, generation])

  const retry = useCallback(() => {
    void dispatch(fetchCompanyDefaultsAccess({ teamId, force: true }))
  }, [dispatch, teamId])

  const status: CompanyDefaultsAccessStatus = access
    ? "ready"
    : sliceStatus === "error" && sliceTeamId === teamId
      ? "error"
      : "loading"

  const can = useCallback(
    (block: CompanyDefaultsBlockKey, action: CompanyDefaultsAction) =>
      canDo(access, block, action),
    [access]
  )
  const canViewBlock = useCallback(
    (block: CompanyDefaultsBlockKey) => canDo(access, block, "view"),
    [access]
  )

  return { status, access, retry, can, canViewBlock }
}

export function useCompanyDefaultsSubtabs() {
  const { access, status } = useCompanyDefaultsAccess()
  const scope = useCompanyDefaultsScope()
  const subtabs = useMemo(
    () =>
      visibleSubtabsFor(access, scope).map((subtab) => ({
        key: COMPANY_DEFAULTS_SUBTAB_ROUTES[subtab],
        label: COMPANY_DEFAULTS_SUBTAB_LABELS[subtab]
      })),
    [access, scope]
  )
  return { subtabs, status }
}

export const allowsSubtab =
  (subtab: CompanyDefaultsSubtab) =>
  (access: CompanyDefaultsAccessResponse, scope: CompanyDefaultsScope) =>
    visibleSubtabsFor(access, scope).includes(subtab)

export const allowsBlock =
  (block: CompanyDefaultsBlockKey, action: CompanyDefaultsAction = "view") =>
  (access: CompanyDefaultsAccessResponse, scope: CompanyDefaultsScope) =>
    isBlockShown(access, block, scope) && canDo(access, block, action)
